import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { number, shape, string } from 'prop-types';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Loader, RadioGroup, SanitizedHtml, Skeleton, useTravelPurchaseContext } from 'components';
import { convertToCents, formatDate, formatLocalizedCurrency } from 'helpers';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { setTravelPurchase } from 'store';

import 'assets/styles/components/travel-purchase/travel-insurance-step.scss';

const InsuranceOption = ({ insurance }) => {
  const { t } = useTranslation();

  if (!insurance) return null;

  return (
    <div className="w-full">
      <div className={clsx('flex-center-between flex-wrap gap-2 xs:flex-nowrap', insurance.price > 0 && ' mb-2')}>
        <p className="text-sm uppercase font-bold mt-1 w-4/5">{insurance.title}</p>
        <p className="text-primary-500 w-1/5 text-right">
          {formatLocalizedCurrency(insurance.price)}
          {insurance.scope === 'Personne' && <span className="uppercase">{t('unitPrice')}</span>}
        </p>
      </div>

      <div
        className={clsx(
          'flex items-end flex-wrap gap-2 md:flex-nowrap',
          insurance?.description ? 'justify-between' : 'justify-end'
        )}
      >
        {insurance.description && <SanitizedHtml className="text-gray-600 md:w-4/5" content={insurance.description} />}

        {insurance.price > 0 && (
          <a
            href={routes.insurance.path}
            target="_blank"
            className="text-secondary-500 underline underline-offset-2 block md:w-1/5 text-right hover:no-underline"
            rel="noreferrer"
          >
            {t('buttons.seeDetails')}
          </a>
        )}
      </div>
    </div>
  );
};

const TravelInsuranceStep = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { currentUser } = useUser();
  const { updateTravelPurchaseLoading } = useTravelPurchaseContext();
  const dispatch = useDispatch();
  const passengersCategories = useSelector((state) => state.travelPurchase.passengersCat);
  const departureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedLodging = useSelector((state) => state.travelPurchase.lodging);
  const storedInsurance = useSelector((state) => state.travelPurchase.insurance);

  const {
    call: insurancesCall,
    data: insurances,
    setData: setInsurances,
    loading: insurancesLoading
  } = useFetch({ withSetState: false });

  const { watch, formState, setValue } = useFormContext();
  const { next } = useTravelPurchaseContext();

  const isValid = watch('insurance') && formState.isValid;
  const travelId = searchParams.get('travelId');
  const typeItem = searchParams.get('typeItem');

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getInsurances();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getInsurances = async () => {
    if (!currentUser?.serialNumber || !travelId || !typeItem) return;

    const nbUsers = passengersCategories.reduce((acc, passenger) => acc + passenger.qty, 0);

    const { data } =
      (await insurancesCall({
        service: 'castelis',
        method: 'get',
        path: `/product/travels/${currentUser.serialNumber}/insurance/${travelId}/${typeItem}`,
        query: {
          productId: travelId,
          typeItem,
          userId: currentUser.serialNumber,
          date: formatDate(departureDate, 'dd/MM/yyyy'),
          nbUsers,
          totalAmount: storedLodging?.price || 0
        }
      })) || {};

    const insurancesData = data?.body?.insurances || [];

    if (!isEmpty(insurancesData)) {
      /*
        Format insurances list from API response to add value, name, uuid and content
        These properties are used to display custom radio label content and for form validations
      */
      const insuranceList = insurancesData.map((insurance) => ({
        ...insurance,
        name: 'insurance',
        value: insurance.id,
        uuid: insurance.id,
        content: () => <InsuranceOption insurance={insurance} />
      }));

      setInsurances(insuranceList);

      if (storedInsurance) {
        const selectedInsurance = insuranceList.find((insurance) => insurance.id === storedInsurance.id);
        if (selectedInsurance) {
          setValue('insurance', selectedInsurance.id, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true
          });
        }
      }
    }
  };

  const handleNextClick = () => {
    const selectedInsurance = insurances?.find((insurance) => insurance.id.toString() === watch('insurance'));

    if (isEmpty(selectedInsurance)) return;
    // Insurance price must be saved in cents
    const insurancePrice = convertToCents(selectedInsurance.price);

    // Save selected insurance in the store
    dispatch(
      setTravelPurchase({
        key: 'insurance',
        value: {
          unitPrice: insurancePrice,
          id: selectedInsurance.id,
          scope: selectedInsurance.scope
        }
      })
    );

    // Validate step and navigate to next step
    next();
  };

  return (
    <div className="travel-insurance-step lg:w-[90%]">
      <h2 className="hidden underlined-blue lg:mb-4 lg:block lg:h2">{t('purchase:insurance.title')}</h2>
      <h3 className="uppercase mb-4">{t('purchase:insurance.subtitle', { appName: appService.brand() })}</h3>

      {insurancesLoading && (
        <>
          <Skeleton classNames="rounded-lg h-24 w-full mb-4" />
          <Skeleton classNames="rounded-lg h-24 w-full mb-4" />
          <Skeleton classNames="rounded-lg h-24 w-full" />
        </>
      )}

      <RadioGroup
        radioOptions={insurances || []}
        rules={{ required: true }}
        className={clsx(insurancesLoading && 'hidden')}
        labelHidden
      />

      <button
        type="button"
        className="btn purchase-btn"
        disabled={!isValid || updateTravelPurchaseLoading}
        onClick={handleNextClick}
      >
        {updateTravelPurchaseLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
        {t('buttons.continue')}
      </button>
    </div>
  );
};

InsuranceOption.propTypes = {
  insurance: shape({
    title: string,
    description: string,
    pricePerson: number
  }).isRequired
};

export default TravelInsuranceStep;
